import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { 
  CheckCircle, 
  Clock, 
  Download, 
  FileText, 
  Users, 
  Calendar,
  MapPin,
  User,
  ArrowLeft,
  Video,
  Eye
} from "lucide-react";

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`;

export default function ReunionDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hovered, setHovered] = useState(null);
  const [updatingTask, setUpdatingTask] = useState(null);

  const getToken = () => localStorage.getItem("access_token");
  const getAuthData = () => JSON.parse(localStorage.getItem("auth_data") || "{}");
  const authData = getAuthData();

  useEffect(() => {
    const fetchReunionDetails = async () => {
      setLoading(true);
      try {
        const token = getToken();
        if (!token) {
          setError("Vous devez être connecté");
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_URL}/reunions/${id}/`, {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });

        if (!response.ok) {
          throw new Error("Erreur lors du chargement des détails");
        }

        const responseData = await response.json();
        console.log("Détails réunion:", responseData);
        
        const tasksResponse = await fetch(`${API_URL}/taches/?reunion=${id}`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        
        let tasks = [];
        if (tasksResponse.ok) {
          const tasksData = await tasksResponse.json();
          tasks = tasksData.results || tasksData;
        }

        const crResponse = await fetch(`${API_URL}/comptes-rendus/?reunion=${id}`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        
        let compteRendu = null;
        if (crResponse.ok) {
          const crData = await crResponse.json();
          const crList = crData.results || crData;
          compteRendu = crList.length > 0 ? crList[0] : null;
        }

        const formattedData = {
          id: responseData.id,
          titre: responseData.titre,
          date: new Date(responseData.date_heure).toLocaleString("fr-FR", {
            day: "2-digit",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
          }),
          date_heure: responseData.date_heure,
          participants: responseData.participants_details || [],
          compteRendu: compteRendu,
          tasks: tasks,
          objectif: responseData.objectif,
          salle: responseData.salle,
          duree: responseData.duree,
          organisateur: responseData.organisateur_nom,
          organisateur_id: responseData.organisateur,
          statut: responseData.statut,
          participations: responseData.participations || []
        };

        setData(formattedData);
      } catch (err) {
        console.error("Erreur:", err);
        setError(err.message || "Erreur de chargement");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchReunionDetails();
    }
  }, [id]);

  const handleView = (crId) => {
    const token = getToken();
    window.open(`${API_URL}/comptes-rendus/${crId}/pdf/?token=${token}`, '_blank');
  };

  const handleDownload = async (crId) => {
    try {
      const token = getToken();
      if (!token) {
        console.error("Vous devez être connecté");
        alert("Vous devez être connecté");
        return;
      }
      
      console.log("Téléchargement du CR depuis détails:", crId);
      
      const downloadUrl = `${API_URL}/comptes-rendus/${crId}/download/?token=${token}`;
      
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Erreur réponse:", response.status, errorText);
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      console.log("Taille du blob:", blob.size);
      console.log("Type du blob:", blob.type);
      
      if (blob.size === 0) {
        throw new Error("Fichier vide");
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compte_rendu_${crId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      console.log("Téléchargement réussi");
      
      const successMsg = document.createElement('div');
      successMsg.style.cssText = 'position:fixed;bottom:20px;right:20px;background:#10b981;color:white;padding:12px 20px;border-radius:8px;z-index:9999';
      successMsg.textContent = 'Téléchargement terminé !';
      document.body.appendChild(successMsg);
      setTimeout(() => successMsg.remove(), 3000);
      
    } catch (error) {
      console.error("Erreur téléchargement:", error);
      alert("Erreur lors du téléchargement: " + error.message);
    }
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    setUpdatingTask(taskId);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/taches/${taskId}/changer_statut/`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ statut: newStatus })
      });

      if (response.ok) {
        setData(prev => ({
          ...prev,
          tasks: prev.tasks.map(task =>
            task.id === taskId ? { ...task, statut: newStatus } : task
          )
        }));
      }
    } catch (err) {
      console.error("Erreur changement statut:", err);
    } finally {
      setUpdatingTask(null);
    }
  };

  const getStatusStyle = (statut) => {
    const styles = {
      planifiee: { bg: "#5e17eb", color: "white", text: "Planifiée" },
      en_cours: { bg: "#22c55e", color: "white", text: "En cours" },
      terminee: { bg: "#9ca3af", color: "white", text: "Terminée" },
      annulee: { bg: "#ef4444", color: "white", text: "Annulée" }
    };
    return styles[statut] || styles.planifiee;
  };

  const getTaskStatusStyle = (statut) => {
    const styles = {
      a_faire: { bg: "#f59e0b", color: "white", text: "À faire" },
      en_cours: { bg: "#5e17eb", color: "white", text: "En cours" },
      terminee: { bg: "#22c55e", color: "white", text: "Terminée" }
    };
    return styles[statut] || styles.a_faire;
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p>Chargement des détails de la réunion...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.errorContainer}>
          <p style={{ color: "#ef4444" }}>❌ {error}</p>
          <button onClick={() => window.location.reload()} style={styles.retryButton}>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const status = getStatusStyle(data.statut);

  return (
    <div style={styles.page}>
      <button onClick={() => navigate(-1)} style={styles.backButton}>
        <ArrowLeft size={16} /> Retour
      </button>

      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>{data.titre}</h1>
          <span style={{
            ...styles.statusBadge,
            backgroundColor: status.bg,
            color: status.color
          }}>
            {status.text}
          </span>
        </div>
        
        {data.statut === 'en_cours' && (
          <button
            onClick={() => navigate(`/reunions/${id}/live`)}
            style={styles.joinButton}
          >
            <Video size={16} /> Rejoindre la réunion
          </button>
        )}
      </div>

      <div style={styles.metaBox}>
        <div style={styles.metaGrid}>
          <div style={styles.metaItem}>
            <Calendar size={16} color="#5e17eb" />
            <div>
              <span style={styles.metaLabel}>Date et heure</span>
              <span style={styles.metaValue}>{data.date}</span>
            </div>
          </div>
          <div style={styles.metaItem}>
            <Clock size={16} color="#5e17eb" />
            <div>
              <span style={styles.metaLabel}>Durée</span>
              <span style={styles.metaValue}>{data.duree} minutes</span>
            </div>
          </div>
          <div style={styles.metaItem}>
            <User size={16} color="#5e17eb" />
            <div>
              <span style={styles.metaLabel}>Organisateur</span>
              <span style={styles.metaValue}>{data.organisateur}</span>
            </div>
          </div>
          <div style={styles.metaItem}>
            <MapPin size={16} color="#5e17eb" />
            <div>
              <span style={styles.metaLabel}>Salle</span>
              <span style={styles.metaValue}>{data.salle}</span>
            </div>
          </div>
        </div>
        {data.objectif && (
          <div style={styles.objectifBox}>
            <strong style={styles.objectifLabel}>Objectif :</strong>
            <p style={styles.objectifText}>{data.objectif}</p>
          </div>
        )}
      </div>

      <div style={styles.box}>
        <div style={styles.boxHeader}>
          <h3 style={styles.boxTitle}>
            <Users size={18} color="#5e17eb" /> Participants ({data.participants.length})
          </h3>
        </div>
        {data.participants.length === 0 ? (
          <p style={styles.muted}>Aucun participant</p>
        ) : (
          <div style={styles.grid}>
            {data.participants.map((p, i) => (
              <div
                key={i}
                style={{
                  ...styles.participantCard,
                  transform: hovered === i ? "translateY(-4px) scale(1.02)" : "translateY(0)",
                  boxShadow:
                    hovered === i
                      ? "0 8px 20px rgba(94,23,235,0.15)"
                      : "0 2px 8px rgba(0,0,0,0.04)",
                }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >
                <div style={styles.avatar}>{p.name?.charAt(0) || "?"}</div>
                <div style={styles.participantInfo}>
                  <p style={styles.participantName}>{p.name}</p>
                  <p style={styles.participantEmail}>{p.email}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={styles.box}>
        <div style={styles.boxHeader}>
          <h3 style={styles.boxTitle}>
            <FileText size={18} color="#5e17eb" /> Compte rendu
          </h3>
        </div>
        {data.compteRendu ? (
          <div style={styles.crContainer}>
            <div style={styles.crInfo}>
              <FileText size={20} color="#5e17eb" />
              <div style={styles.crDetails}>
                <span style={styles.crTitle}>{data.compteRendu.resume?.substring(0, 100)}...</span>
                <span style={styles.crDate}>
                  Créé le {new Date(data.compteRendu.created_at).toLocaleDateString('fr-FR')}
                </span>
              </div>
            </div>
            <div style={styles.crActions}>
              <button
                onClick={() => handleView(data.compteRendu.id)}
                style={styles.viewButton}
                title="Voir"
              >
                <Eye size={16} /> Voir
              </button>
              <button
                onClick={() => handleDownload(data.compteRendu.id)}
                style={styles.downloadButton}
                title="Télécharger"
              >
                <Download size={16} /> Télécharger
              </button>
            </div>
          </div>
        ) : (
          <p style={styles.muted}>Aucun compte-rendu disponible</p>
        )}
      </div>

      <div style={styles.box}>
        <div style={styles.boxHeader}>
          <h3 style={styles.boxTitle}>Tâches ({data.tasks.length})</h3>
          {(authData.role === 'Admin' || authData.role === 'Manager' || authData.userId === data.organisateur_id) && (
            <button
              onClick={() => navigate(`/taches/nouveau?reunion=${id}`)}
              style={styles.addButton}
            >
              + Nouvelle tâche
            </button>
          )}
        </div>
        
        {data.tasks.length === 0 ? (
          <p style={styles.muted}>Aucune tâche associée</p>
        ) : (
          <div style={styles.tasksList}>
            {data.tasks.map((task) => {
              const taskStatus = getTaskStatusStyle(task.statut);
              const isUpdating = updatingTask === task.id;
              
              return (
                <div key={task.id} style={styles.taskCard}>
                  <div style={styles.taskHeader}>
                    <span style={styles.taskDescription}>{task.description}</span>
                    <span style={{
                      ...styles.taskStatusBadge,
                      backgroundColor: taskStatus.bg,
                      color: taskStatus.color
                    }}>
                      {taskStatus.text}
                    </span>
                  </div>
                  
                  <div style={styles.taskDetails}>
                    <span style={styles.taskAssignee}>
                      <User size={12} color="#666" /> {task.responsable_nom || "Non assigné"}
                    </span>
                    {task.deadline && (
                      <span style={styles.taskDeadline}>
                        <Clock size={12} color="#666" /> {new Date(task.deadline).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </div>

                  {authData.userId === task.responsable && task.statut !== 'terminee' && (
                    <select
                      value={task.statut}
                      onChange={(e) => handleTaskStatusChange(task.id, e.target.value)}
                      disabled={isUpdating}
                      style={{
                        ...styles.taskSelect,
                        backgroundColor: taskStatus.bg,
                        color: taskStatus.color
                      }}
                    >
                      <option value="a_faire">À faire</option>
                      <option value="en_cours">En cours</option>
                      <option value="terminee">Terminée</option>
                    </select>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    background: "#f7f6ff",
    padding: "22px",
    borderRadius: "16px",
    minHeight: "100vh",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    gap: "16px"
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "3px solid #f3f3f3",
    borderTop: "3px solid #5e17eb",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  },
  errorContainer: {
    textAlign: "center",
    padding: "40px",
    background: "white",
    borderRadius: "12px"
  },
  retryButton: {
    marginTop: "12px",
    padding: "8px 16px",
    background: "#5e17eb",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer"
  },
  backButton: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 16px",
    background: "white",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    color: "#666",
    fontSize: "14px",
    cursor: "pointer",
    marginBottom: "18px"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "18px",
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
    flexWrap: "wrap",
    gap: "16px"
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap"
  },
  title: { 
    fontSize: "24px", 
    color: "#1f1f2e", 
    margin: 0,
    fontWeight: "500"
  },
  statusBadge: {
    padding: "6px 12px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "500",
    display: "inline-flex",
    alignItems: "center"
  },
  joinButton: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 20px",
    background: "#22c55e",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer"
  },
  metaBox: {
    background: "white",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
    marginBottom: "18px"
  },
  metaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "16px"
  },
  metaItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  metaLabel: {
    display: "block",
    fontSize: "11px",
    color: "#888",
    marginBottom: "2px"
  },
  metaValue: {
    display: "block",
    fontSize: "14px",
    color: "#1f1f2e",
    fontWeight: "500"
  },
  objectifBox: {
    borderTop: "1px solid #e5e7eb",
    paddingTop: "16px"
  },
  objectifLabel: {
    fontSize: "13px",
    color: "#5e17eb",
    display: "block",
    marginBottom: "4px"
  },
  objectifText: {
    fontSize: "14px",
    color: "#4b5563",
    margin: 0,
    lineHeight: "1.5"
  },
  box: {
    background: "white",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
    marginBottom: "18px"
  },
  boxHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    flexWrap: "wrap",
    gap: "12px"
  },
  boxTitle: { 
    fontSize: "16px", 
    color: "#5e17eb", 
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontWeight: "600"
  },
  addButton: {
    background: "transparent",
    border: "1px solid #5e17eb",
    color: "#5e17eb",
    padding: "6px 12px",
    borderRadius: "6px",
    fontSize: "12px",
    cursor: "pointer"
  },
  grid: { 
    display: "grid", 
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", 
    gap: "12px" 
  },
  participantCard: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    borderRadius: "8px",
    transition: "all 0.2s ease",
    cursor: "pointer",
    background: "#f9f9ff",
    border: "1px solid #e5e7eb"
  },
  avatar: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    background: "#5e17eb",
    color: "white",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: "bold",
    fontSize: "14px"
  },
  participantInfo: {
    flex: 1
  },
  participantName: { 
    fontWeight: "500", 
    color: "#1f1f2e", 
    fontSize: "13px",
    margin: 0
  },
  participantEmail: { 
    fontSize: "11px", 
    color: "#888",
    margin: 0,
    marginTop: "2px"
  },
  crContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap"
  },
  crInfo: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1
  },
  crDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "2px"
  },
  crTitle: {
    fontSize: "14px",
    color: "#1f1f2e",
    fontWeight: "500"
  },
  crDate: {
    fontSize: "11px",
    color: "#888"
  },
  crActions: {
    display: "flex",
    gap: "8px"
  },
  viewButton: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 12px",
    background: "#dbeafe",
    color: "#2563eb",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    cursor: "pointer"
  },
  downloadButton: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 12px",
    background: "#e6f7e6",
    color: "#16a34a",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    cursor: "pointer"
  },
  muted: { 
    color: "#888", 
    fontSize: "14px",
    margin: 0
  },
  tasksList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px"
  },
  taskCard: {
    background: "#f9f9ff",
    borderRadius: "8px",
    padding: "16px",
    border: "1px solid #e5e7eb"
  },
  taskHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
    gap: "12px",
    flexWrap: "wrap"
  },
  taskDescription: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#1f1f2e"
  },
  taskStatusBadge: {
    padding: "4px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "500"
  },
  taskDetails: {
    display: "flex",
    gap: "16px",
    marginBottom: "12px",
    fontSize: "12px",
    color: "#666"
  },
  taskAssignee: {
    display: "flex",
    alignItems: "center",
    gap: "4px"
  },
  taskDeadline: {
    display: "flex",
    alignItems: "center",
    gap: "4px"
  },
  taskSelect: {
    width: "100%",
    padding: "8px",
    borderRadius: "6px",
    border: "none",
    fontSize: "12px",
    fontWeight: "500",
    cursor: "pointer",
    marginTop: "8px"
  }
};

const styleSheet = document.createElement("style");
styleSheet.innerText = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);